import React from 'react';
import styled, { keyframes } from 'styled-components';
import { X, AlertCircle, CheckCircle, Info, Sparkles } from 'lucide-react';

const overlayFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const modalScaleIn = keyframes`
  from { opacity: 0; transform: translateY(-12px) scale(0.96); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  animation: ${overlayFadeIn} 0.2s ease;
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  padding: 28px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  border: 1px solid #e2e8f0;
  position: relative;
  animation: ${modalScaleIn} 0.25s ease;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding-right: 32px;
`;

const IconWrapper = styled.div`
  margin-right: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const Title = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #0f172a;
  display: flex;
  align-items: center;
`;

const Message = styled.p`
  margin: 0 0 24px 0;
  color: #64748b;
  line-height: 1.6;
  font-size: 14px;
  white-space: pre-line;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  &.primary {
    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
    color: white;
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.35);
    
    &:hover {
      background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%);
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
      transform: translateY(-1px);
    }
    
    &:active {
      transform: translateY(0);
    }
  }
  
  &.secondary {
    background: #f1f5f9;
    color: #475569;
    border: 1px solid #e2e8f0;
    
    &:hover {
      background: #e2e8f0;
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  color: #64748b;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #f1f5f9;
    color: #334155;
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const AlertModal = ({ 
  isOpen, 
  onClose, 
  title, 
  message, 
  type = 'info',
  onConfirm,
  confirmText = '确定',
  showCancel = false,
  cancelText = '取消'
}) => {
  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle color="#10b981" />;
      case 'error':
        return <AlertCircle color="#ef4444" />;
      case 'warning':
        return <AlertCircle color="#f59e0b" />;
      case 'ai':
        return <Sparkles color="#6366f1" />;
      default:
        return <Info color="#3b82f6" />;
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <X />
        </CloseButton>
        
        <Header>
          <IconWrapper>
            {getIcon()}
          </IconWrapper>
          <Title>{title}</Title>
        </Header>
        
        <Message>{message}</Message>
        
        <ButtonGroup>
          {showCancel && (
            <Button className="secondary" onClick={onClose}>
              {cancelText}
            </Button>
          )}
          <Button className="primary" onClick={handleConfirm}>
            {confirmText}
          </Button>
        </ButtonGroup>
      </Modal>
    </Overlay>
  );
};

export default AlertModal;
